import { useEffect, useRef, useState } from 'react';

import type { RockDTO } from '@/api/endpoints';
import { Icon } from '@/components/ui/Icon';
import { ROCK_CELL, ROCK_SIZE, ROCK_URL, rockFrame } from '@/game/data/rockAtlas';
import { placementOf } from '@/game/engine/field';
import { inReach, project, type Viewport } from '@/game/engine/projection';
import { rockShading } from './RockField';
import { useRun } from '@/store/run';

/**
 * A rock placed in the corridor, projected from `(u, v, d)`.
 *
 * Mock panels 5–7. Three states the art has to carry:
 *   locked    — a card says which axe is needed; tapping bounces
 *   in reach  — tappable, crack stages advance, hit dots below
 *   too far   — dimmed and inert, with a label saying so
 *
 * The treasure tell is unchanged from the flat view and must stay that way: both
 * visual types share one silhouette, and only faint gold veining at
 * `lamp.glow_pct` distinguishes them. That near-invisibility is the mechanic.
 */
const ROCK_COLS = ROCK_SIZE.width / ROCK_CELL;
const ROCK_ROWS = ROCK_SIZE.height / ROCK_CELL;

/** The sheet cell for a material at a crack stage, as background percentages. */
function rockSprite(materialKey: string, crackStage: number) {
  const stage = Math.max(0, Math.min(4, Math.round(crackStage)));
  const frame = rockFrame(`renders/rock_${materialKey}_${stage}`);
  if (!frame) return null;
  return {
    px: ROCK_COLS > 1 ? (frame.x / (ROCK_SIZE.width - ROCK_CELL)) * 100 : 0,
    py: ROCK_ROWS > 1 ? (frame.y / (ROCK_SIZE.height - ROCK_CELL)) * 100 : 0,
  };
}

/** Deterministic 0–1 from any pair of integers. Same hash WallRocks/CaveDecor use. */
function noise(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) >>> 0;
  h = ((h ^ (h >>> 13)) * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Chip debris + a dust puff for one landed swing.
 *
 * `seed` reseeds the particle angles/distances per hit so two consecutive
 * bursts on the same rock don't look identical, without reaching for
 * `Math.random()` — the rest of the corridor derives its scatter the same
 * deterministic way (see WallRocks/CaveDecor).
 */
function HitBurst({ seed, big }: { seed: number; big: boolean }) {
  const count = big ? 8 : 5;
  const chips = Array.from({ length: count }, (_, i) => {
    const angle = noise(seed, i) * 360;
    const dist = (big ? 22 : 14) + noise(seed, i + 97) * (big ? 14 : 8);
    const delay = noise(seed, i + 211) * 60;
    const size = (big ? 3.2 : 2.2) + noise(seed, i + 331) * 1.6;
    return { angle, dist, delay, size };
  });

  return (
    <span className="pointer-events-none absolute inset-0 z-20" aria-hidden>
      <span
        className="absolute left-1/2 top-1/2 rounded-full bg-[radial-gradient(circle,rgba(230,190,140,.5),transparent_70%)]"
        style={{
          width: big ? '80%' : '55%',
          height: big ? '80%' : '55%',
          animation: 'dustPuff .5s ease-out forwards',
        }}
      />
      {chips.map((c, i) => (
        <span
          key={i}
          className="absolute left-1/2 top-1/2 rounded-[1px] bg-[#EED7A8]"
          style={{
            width: c.size,
            height: c.size,
            // @ts-expect-error -- custom properties read by the chipBurst keyframes
            '--a': `${c.angle}deg`,
            '--d': `${c.dist}px`,
            animation: `chipBurst .42s ease-out ${c.delay}ms forwards`,
          }}
        />
      ))}
    </span>
  );
}

export function CorridorRock({ rock, depth, crackStage, view, lightPx, onSwing }: {
  rock: RockDTO;
  /** Metres ahead of the camera. */
  depth: number;
  crackStage: number;
  view: Viewport;
  lightPx: number;
  onSwing: () => void;
}) {
  const lastCrit = useRun((s) => s.lastCrit);
  const lastHeavySwing = useRun((s) => s.lastHeavySwing);

  /* Chip debris on every landed swing — CorridorRock otherwise only shows a
     crack-stage SVG and, on crit/heavy, a text toast, so an ordinary tap (most
     of them) read as silent. `prevHit` detects the swing itself rather than
     any crackStage change, since a stage does not advance on every hit. */
  const prevHit = useRef({ uuid: rock.spawn_uuid, hits: rock.hits_registered });
  const burstId = useRef(0);
  const [burst, setBurst] = useState<{ id: number; seed: number; big: boolean } | null>(null);

  useEffect(() => {
    const sameRock = prevHit.current.uuid === rock.spawn_uuid;
    if (sameRock && rock.hits_registered > prevHit.current.hits) {
      burstId.current += 1;
      setBurst({
        id: burstId.current,
        seed: rock.hits_registered * 97 + rock.spawn_uuid.charCodeAt(0),
        big: Boolean(lastCrit || lastHeavySwing)
          || rock.hits_registered >= (rock.required_hits ?? Infinity),
      });
    } else if (!sameRock) {
      setBurst(null);
    }
    prevHit.current = { uuid: rock.spawn_uuid, hits: rock.hits_registered };
  }, [rock.spawn_uuid, rock.hits_registered, rock.required_hits, lastCrit, lastHeavySwing]);

  useEffect(() => {
    if (!burst) return;
    const t = setTimeout(() => setBurst(null), 500);
    return () => clearTimeout(t);
  }, [burst]);

  /* The server placed this rock. See `placementOf` for what reading it from the uuid
     instead was costing — in short, the blocker was drawn against the wall. */
  const { slot, u, v } = placementOf(rock);
  const p = project(u, v, depth, view, lightPx);

  /* Fully fogged rocks are not rendered at all — a 0-opacity button is still a
     tap target, and an invisible tap target is worse than no rock. */
  if (p.opacity <= 0.02) return null;

  const reachable = inReach(depth) && !rock.locked;
  const isTreasure = rock.visual_type === 'treasure_rock';
  /* One sprite per material per stage. `crackStage` is 0-5 from the server (5 is
     "broken", which the loot card covers), so it is clamped to the four damaged
     renders. */
  const sprite = rockSprite(rock.material?.key ?? 'loose', crackStage);

  const a = rock.material?.color_a ?? '#6E5240';
  const b = rock.material?.color_b ?? '#2C1E16';

  /* Path rocks are the blockers, so they read bigger — mock panel 5 has one
     filling the corridor. */
  const base = slot === 'path' ? 150 : 92;
  const size = base * p.scale;
  const worked = crackStage > 0;

  /* No name, here or in the plate below. What is inside a rock is the surprise the loop is
     built on, and the corridor answered it before the first swing. The axe level stays: that
     is a hard gate the player has to be able to read, and it is about hardness, not loot. */
  const label = rock.locked
    ? `Rock, needs axe level ${rock.required_axe_level}`
    : reachable
      ? 'Swing at the rock'
      : 'Rock, too far to reach';

  return (
    <button
      type="button"
      onPointerDown={reachable ? onSwing : undefined}
      disabled={!reachable}
      aria-label={label}
      className={`pointer-events-auto absolute border-0 bg-transparent p-0
                  transition-[transform,opacity] duration-[420ms]
                  ${reachable ? 'cursor-pointer active:scale-95' : 'cursor-not-allowed'}`}
      style={{
        left: p.x,
        top: p.y,
        width: size,
        height: size,
        opacity: p.opacity * (reachable ? 1 : 0.55),
        zIndex: p.z,
        transform: 'translate(-50%, -50%)',
        /* The most-tapped element in the game, and a div rather than a button,
           so the global rule does not reach it. Rapid tapping here is the
           core verb; a double-tap-zoom mid-swing is the worst case. */
        touchAction: 'manipulation',
        transitionTimingFunction: 'cubic-bezier(.22,.9,.24,1)',
      }}
    >
      {sprite ? (
        /*
         * The Blender render for this material at this crack stage.
         *
         * The damage is real geometry — bites are cut out of the mesh and chips
         * fall beside it — so the silhouette itself tells you how close the rock
         * is to breaking. That is the game's core feedback, and a crack drawn on
         * an intact boulder cannot carry it: it still reads as an intact boulder
         * with a line on it.
         *
         * Percentage sizing, because this element is scaled by the corridor's
         * projection and has no fixed pixel size. See MinerBack for the same math.
         */
        <span
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${ROCK_URL})`,
            backgroundSize: `${ROCK_COLS * 100}% ${ROCK_ROWS * 100}%`,
            backgroundPosition: `${sprite.px}% ${sprite.py}%`,
            backgroundRepeat: 'no-repeat',
            /* Same directional torch shading RockField gives every other spawn — this
               is the one rock in the corridor big enough that a flat, unshaded sprite
               reads as pasted on rather than standing in the light with everything
               around it. See `rockShading` in RockField.tsx. */
            filter: rockShading({ x: p.x, y: p.y, size, view, worked, tappable: reachable }),
          }}
          aria-hidden
        />
      ) : (
        <span
          className="absolute inset-0 rounded-[47%_53%_51%_49%/52%_46%_54%_48%]
                     shadow-[inset_-5px_-6px_12px_rgba(0,0,0,.6),inset_4px_5px_10px_rgba(255,196,140,.16)]"
          style={{ background: `radial-gradient(62% 56% at 34% 28%, ${a} 0%, ${b} 74%, #120C08 100%)` }}
          aria-hidden
        />
      )}

      {burst && <HitBurst key={burst.id} seed={burst.seed} big={burst.big} />}

      {/* the treasure tell — gold veining at lamp glow, plus one slow sparkle */}
      {isTreasure && !sprite && (
        <>
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 60 60"
               style={{ opacity: rock.glow_pct / 100 }} aria-hidden>
            <path d="M18 16 L26 30 L21 42" stroke="#E8B24C" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            <path d="M38 13 L42 28 L50 34" stroke="#E8B24C" strokeWidth="1.3" fill="none" strokeLinecap="round" />
          </svg>
          <span
            className="absolute left-[60%] top-[38%] h-1.5 w-1.5 animate-pulse rounded-full
                       bg-torch-core shadow-[0_0_9px_2px_rgba(255,230,168,.85)]"
            aria-hidden
          />
        </>
      )}

      {/* damage stages */}
      {crackStage > 0 && reachable && !sprite && (
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 60 60" aria-hidden>
          {crackStage >= 1 && <path d="M30 8 L26 24 L34 32" stroke="#080503" strokeWidth="2.6" fill="none" />}
          {crackStage >= 2 && <path d="M12 30 L28 27 L22 44" stroke="#080503" strokeWidth="2.6" fill="none" />}
          {crackStage >= 3 && <path d="M48 22 L34 30 L44 46" stroke="#080503" strokeWidth="2.6" fill="none" />}
          {crackStage >= 4 && <path d="M8 14 L24 22 M52 44 L38 38" stroke="#080503" strokeWidth="2" fill="none" />}
        </svg>
      )}

      {/* Heavy Swing or Critical Hit feedback */}
      {reachable && (lastCrit || lastHeavySwing) && (
        <span
          className="absolute left-1/2 top-[-28px] -translate-x-1/2 animate-pulse whitespace-nowrap
                     rounded px-2 py-0.5 font-pixel text-[7px] font-extrabold tracking-wider
                     shadow-[0_0_12px_rgba(245,197,66,.8)] z-10"
          style={{
            backgroundColor: lastCrit ? '#FF5FA2' : '#F5C542',
            color: '#0B0705',
          }}
        >
          <span className="inline-flex items-center gap-1">
            <Icon name={lastCrit ? 'sparkle' : 'energy'} size={9} />
            {lastCrit ? 'CRITICAL!' : 'HEAVY SWING!'}
          </span>
        </span>
      )}

      {/* the gate, on the rock — mock panel 5 */}
      {p.scale > 0.45 && (
        <span
          className={`absolute left-1/2 top-[-12px] -translate-x-1/2 whitespace-nowrap rounded-md
                      border px-1.5 py-0.5 font-pixel text-[6px] tracking-wide
                      ${rock.locked
                        ? 'border-[rgba(224,85,63,.5)] bg-black/90 text-[#FF9A8B]'
                        : 'border-[rgba(92,203,106,.4)] bg-black/85 text-[var(--ok)] opacity-60'}`}
        >
          LV.{rock.required_axe_level}
        </span>
      )}

      {/* hit dots — mock panels 6 and 7 */}
      {reachable && rock.required_hits != null && (
        <span className="absolute inset-x-0 bottom-[-16px] flex justify-center gap-1" aria-hidden>
          {Array.from({ length: rock.required_hits }, (_, i) => (
            <i key={i} className={`h-1.5 w-1.5 rounded-full ${
              i < rock.hits_registered
                ? 'bg-[var(--ok)] shadow-[0_0_6px_rgba(92,203,106,.7)]'
                : 'bg-[#3A2A1E]'}`} />
          ))}
        </span>
      )}
    </button>
  );
}

/**
 * Which slot a rock occupies.
 *
 * Until CAVE_VIEW phase 3 gives the server `slot`/`slot_u`/`slot_v`, this derives
 * it: a rock that blocks movement is a path rock, everything else goes on a wall.
 * The server already decides *which* rocks block, so this stays consistent with it.
 */

